/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks\useCustomerCodeForm.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Class of Services module.
────────────────────────────────────────────────────────────────*/


import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useContextStore } from '../../../store/context';
import { createCustomerCode, updateCustomerCode } from '../api';
import { getDefaultFormState } from '../utils';
import { validateCustomerCodeForm } from '../utils/validators';
import type { FormState } from '../types';

interface UseCustomerCodeFormParams {
  id?: string;
  duplicateId?: string | null;
  initialData: FormState | null;
  isEditMode: boolean;
  isDuplicateMode: boolean;
}

export function useCustomerCodeForm({
  id,
  duplicateId,
  initialData,
  isEditMode,
  isDuplicateMode
}: UseCustomerCodeFormParams) {
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { selectedTenant, selectedDomain } = useContextStore();
  const tenantSlug = selectedTenant?.slug ?? 'main';

  const defaultFormState = useMemo(() => getDefaultFormState(), []);
  const initializedRef = useRef(false);
  const lastInitialDataRef = useRef<string | null>(null);

  const [formData, setFormData] = useState<FormState>(defaultFormState);
  const [originalData, setOriginalData] = useState<FormState>(defaultFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const showErrorRef = useRef(showError);
  const showInfoRef = useRef(showInfo);
  useEffect(() => {
    showErrorRef.current = showError;
    showInfoRef.current = showInfo;
  }, [showError, showInfo]);

  // Synchronize formData with initialData
  useEffect(() => {
    if (initialData) {
      const initialDataStr = JSON.stringify(initialData);

      // Only update if initialData actually changed
      if (lastInitialDataRef.current !== initialDataStr) {
        lastInitialDataRef.current = initialDataStr;
        setFormData(initialData);
        if (!isDuplicateMode) {
          setOriginalData(initialData);
        }
        initializedRef.current = true;
      }
    } else if (!initializedRef.current && !isEditMode && !isDuplicateMode) {
      // Only set default for new entries when not initialized
      if (lastInitialDataRef.current !== 'default') {
        lastInitialDataRef.current = 'default';
        setFormData(defaultFormState);
        setOriginalData(defaultFormState);
        initializedRef.current = true;
      }
    }
  }, [initialData, isEditMode, isDuplicateMode, defaultFormState]);

  const handleChange = useCallback(<K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field as string]) {
        return prev;
      }
      const updated = { ...prev };
      delete updated[field as string];
      return updated;
    });
  }, []);

  const validateForm = useCallback(() => {
    const validationErrors = validateCustomerCodeForm(
      formData,
      selectedDomain?.id ?? null,
      t
    );

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      showErrorRef.current(t('customerCodes.messages.validationError'));
      return false;
    }

    return true;
  }, [formData, selectedDomain?.id, t]);

  const hasChanges = useMemo(() => {
    if (!isEditMode) {
      return true;
    }
    return (
      formData.code.trim() !== originalData.code.trim() ||
      formData.description.trim() !== originalData.description.trim() ||
      formData.active !== originalData.active
    );
  }, [formData, originalData, isEditMode]);

  // Handle save - Always-Active Save Standard: always attempt to save
  const handleSave = useCallback(async (): Promise<FormState & { id?: number; uuid?: string } | undefined> => {
    if (!validateForm()) {
      return undefined;
    }

    setIsSaving(true);
    try {
      const domainId = selectedDomain?.id ?? null;
      if (!domainId) {
        showErrorRef.current(t('customerCodes.messages.domainMissing'));
        setIsSaving(false);
        return undefined;
      }

      const trimmedDescription = formData.description.trim();

      const payload = {
        code: formData.code.trim(),
        description: trimmedDescription || null,
        active: formData.active
      };

      let persisted;
      if (!isEditMode || isDuplicateMode) {
        persisted = await createCustomerCode(
          {
            ...payload,
            domainId
          },
          tenantSlug
        );
      } else {
        persisted = await updateCustomerCode(id!, payload, tenantSlug);
      }

      showSuccess(t('customerCodes.messages.saveSuccess'));

      const savedData: FormState = {
        code: formData.code.trim(),
        description: trimmedDescription,
        active: formData.active
      };

      setOriginalData(savedData);

      // Return data with ID for navigation
      return { ...savedData, id: persisted?.id, uuid: persisted?.uuid } as any;
    } catch (error) {
      const message = error instanceof Error ? error.message : t('customerCodes.messages.saveError');
      showErrorRef.current(message);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [
    formData,
    hasChanges,
    id,
    isDuplicateMode,
    isEditMode,
    selectedDomain?.id,
    showSuccess,
    t,
    tenantSlug,
    validateForm
  ]);

  return {
    formData,
    setFormData,
    originalData,
    errors,
    isSaving,
    hasChanges,
    handleChange,
    handleSave,
    validateForm
  };
}

